import { useField, useFormikContext } from 'formik';
import { Input, PasswordInput } from '@uds/input';
import { forwardRef, Children, useMemo } from 'react';
import { jsx } from 'react/jsx-runtime';
import { Checkbox, MultiCheckbox } from '@uds/checkbox';
import { Radio, MultiRadio } from '@uds/radio';
import { Select } from '@uds/select';
import { Switch, MultiSwitch } from '@uds/switch';
import memoize from 'lodash.memoize';
import { Datepicker } from '@uds/datepicker';
import { Textarea } from '@uds/textarea';

function useError(_ref) {
  let {
    error,
    touched,
    initialTouched,
    initialError
  } = _ref;
  if (touched) {
    return error;
  }
  if (initialTouched) {
    return initialError;
  }
  return undefined;
}

const FormikInput = /*#__PURE__*/forwardRef((props, ref) => {
  const [field, meta] = useField(props.name);
  return /*#__PURE__*/jsx(Input, {
    ...props,
    ...field,
    error: useError(meta),
    ref: ref
  });
});
FormikInput.displayName = 'FormikInput';
var FormikInput$1 = FormikInput;

const FormikPasswordInput = /*#__PURE__*/forwardRef((props, ref) => {
  const [field, meta] = useField(props.name);
  return /*#__PURE__*/jsx(PasswordInput, {
    ...props,
    ...field,
    error: useError(meta),
    ref: ref
  });
});
FormikPasswordInput.displayName = 'FormikPasswordInput';
var FormikPasswordInput$1 = FormikPasswordInput;

const FormikCheckbox = props => {
  const [field, {
    error
  }] = useField({
    name: props.name,
    type: 'checkbox',
    value: props.value,
    validate: props.validate
  });
  // @ts-ignore: update Checkbox to accept and show an error
  return /*#__PURE__*/jsx(Checkbox, {
    ...props,
    error: error,
    ...field
  });
};
var FormikCheckbox$1 = FormikCheckbox;

const FormikMultiCheckbox = props => {
  const [field, {
    error
  }] = useField(props.name);
  const {
    setFieldValue
  } = useFormikContext();
  const handleChange = value => {
    var _field$value;
    const values = (_field$value = field.value) != null ? _field$value : [''];
    const index = values.indexOf(value);
    if (index === -1) {
      values.push(value);
    } else {
      values.splice(index, 1);
    }
    setFieldValue(field.name, values);
  };
  return /*#__PURE__*/jsx(MultiCheckbox, {
    label: props.label
    // @ts-ignore: update MultiCheckbox to accept and show an error
    ,
    error: error,
    disabled: props.disabled,
    readOnly: props.readOnly,
    children: Children.map(props.children, (option, i) => /*#__PURE__*/jsx(option.type, {
      ...option.props,
      id: `${field.name}_${i}`,
      name: field.name,
      checked: field.value.indexOf(option.props.value) > -1,
      onChange: () => handleChange(option.props.value)
    }))
  });
};
var FormikMultiCheckbox$1 = FormikMultiCheckbox;

const FormikRadio = props => {
  const [field, {
    error
  }] = useField({
    name: props.name,
    type: 'radio',
    value: props.value,
    validate: props.validate
  });
  // @ts-ignore: update Radio to accept and show an error
  return /*#__PURE__*/jsx(Radio, {
    ...props,
    error: error,
    ...field
  });
};
var FormikRadio$1 = FormikRadio;

const FormikMultiRadio = props => {
  const [field, {
    error
  }, {
    setValue
  }] = useField(props.name);
  const handleChange = function () {
    let value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    setValue(value);
  };
  return /*#__PURE__*/jsx(MultiRadio, {
    label: props.label
    // @ts-ignore: update MultiRadio to accept and show an error
    ,
    error: error,
    disabled: props.disabled,
    readOnly: props.readOnly,
    children: Children.map(props.children, (option, i) => /*#__PURE__*/jsx(option.type, {
      ...option.props,
      id: `${field.name}_${i}`,
      name: field.name,
      checked: field.value.indexOf(option.props.value) > -1,
      onChange: () => handleChange(option.props.value)
    }))
  });
};
var FormikMultiRadio$1 = FormikMultiRadio;

const FormikSelect = props => {
  const [field, meta, {
    setValue
  }] = useField(props.name);
  const handleChange = _ref => {
    let {
      target: {
        value: newValue
      }
    } = _ref;
    setValue(newValue);
  };
  return /*#__PURE__*/jsx(Select, {
    ...field,
    ...props,
    error: useError(meta),
    onChange: handleChange
  });
};
var FormikSelect$1 = FormikSelect;

const FormikSwitch = /*#__PURE__*/forwardRef((props, ref) => {
  const [field] = useField({
    name: props.name,
    type: 'checkbox'
  });
  return /*#__PURE__*/jsx(Switch, {
    ...props,
    ...field,
    ref: ref,
    on: field.checked
  });
});
FormikSwitch.displayName = 'FormikSwitch';
var FormikSwitch$1 = FormikSwitch;

const FormikMultiSwitch = /*#__PURE__*/forwardRef((props, ref) => {
  const [field, meta, {
    setValue
  }] = useField(props);
  const {
    value
  } = field;
  const handleChange = val => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const values = value || [];
    const index = values.indexOf(val);
    if (index === -1) {
      values.push(val);
    } else {
      values.splice(index, 1);
    }
    setValue(values);
  };
  const {
    label,
    children,
    disabled
  } = props;
  return /*#__PURE__*/jsx(MultiSwitch, {
    hasError: Boolean(useError(meta)),
    label: label,
    disabled: disabled,
    ref: ref,
    children: Children.map(children, (option, i) => {
      var _field$value;
      const value = option.props.value;
      const checked = (_field$value = field.value) === null || _field$value === void 0 ? void 0 : _field$value.includes(value);
      return /*#__PURE__*/jsx(option.type, {
        ...option.props,
        id: `${field.name}_${i}`,
        ref: ref,
        name: `${field.name}`,
        on: checked,
        checked: checked,
        onChange: () => handleChange(value)
      });
    })
  });
});
FormikMultiSwitch.displayName = 'FormikMultiSwitch';
var FormikMultiSwitch$1 = FormikMultiSwitch;

var TimePeriods;
(function (TimePeriods) {
  TimePeriods["DAY"] = "0";
  TimePeriods["MONTH"] = "1";
  TimePeriods["YEAR"] = "2";
})(TimePeriods || (TimePeriods = {}));
const FormikDatepicker = props => {
  const {
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const [{
    value: currentValue
  }, meta] = useField(props.name);
  const currentDate = useMemo(() => currentValue != null ? new Date(currentValue) : new Date(), [currentValue]);
  const handleChange = useMemo(() => memoize(timePeriod => _ref => {
    let {
      target: {
        value: newPeriodValue
      }
    } = _ref;
    const newDate = new Date(currentDate);
    switch (timePeriod) {
      case TimePeriods.YEAR:
        newDate.setFullYear(Number(newPeriodValue));
        break;
      case TimePeriods.MONTH:
        newDate.setMonth(Number(newPeriodValue) - 1);
        break;
      case TimePeriods.DAY:
        newDate.setDate(Number(newPeriodValue));
        break;
    }
    setFieldTouched(props.name, true);
    setFieldValue(props.name, newDate);
  }), [currentDate, setFieldTouched, setFieldValue, props.name]);
  return /*#__PURE__*/jsx(Datepicker, {
    ...props,
    error: useError(meta),
    selectedDay: currentDate.getDate(),
    selectedMonth: currentDate.getMonth() + 1,
    selectedYear: currentDate.getFullYear(),
    onDayChange: handleChange(TimePeriods.DAY),
    onMonthChange: handleChange(TimePeriods.MONTH),
    onYearChange: handleChange(TimePeriods.YEAR)
  });
};
var FormikDatepicker$1 = FormikDatepicker;

const FormikTextarea = props => {
  const [field, meta, {
    setValue
  }] = useField(props.name);
  const handleChange = _ref => {
    let {
      target: {
        value: newValue
      }
    } = _ref;
    setValue(newValue);
  };
  return /*#__PURE__*/jsx(Textarea, {
    ...field,
    ...props,
    error: useError(meta),
    onChange: handleChange
  });
};
var FormikTextarea$1 = FormikTextarea;

export { FormikCheckbox$1 as FormikCheckBox, FormikDatepicker$1 as FormikDatePicker, FormikInput$1 as FormikInput, FormikMultiCheckbox$1 as FormikMultiCheckbox, FormikMultiRadio$1 as FormikMultiRadio, FormikMultiSwitch$1 as FormikMultiSwitch, FormikPasswordInput$1 as FormikPasswordInput, FormikRadio$1 as FormikRadio, FormikSelect$1 as FormikSelect, FormikSwitch$1 as FormikSwitch, FormikTextarea$1 as FormikTextarea };
